import { getCurrencyFormatter } from "@redotech/money/util";
import { THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER } from "@redotech/redo-conversion-core-common/checkouts/widget";
import type { ProgressBar as ProgressBarType } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

interface CartInfo {
  currency: string;
  subtotal_price?: number;
  items: { line_price?: number }[];
}

function useThresholdProgress({
  cart,
  threshold,
}: {
  cart: CartInfo | undefined;
  threshold: ProgressBarType["threshold"];
}) {
  return useMemo(() => {
    if (!cart) {
      return null;
    }

    if (cart.currency !== threshold.currency) {
      return null;
    }

    const subtotal =
      cart.subtotal_price ??
      cart.items.reduce((sum, item) => sum + (item.line_price ?? 0), 0);

    const amountLeft = Math.max(threshold.amount - subtotal, 0);

    const progressPercent = Math.min((subtotal / threshold.amount) * 100, 100);

    const currencyFormatter = getCurrencyFormatter(threshold.currency);
    const formattedAmountLeft = currencyFormatter.format(amountLeft);

    return { amountLeft, formattedAmountLeft, progressPercent };
  }, [cart, threshold]);
}

export const ProgressBar = memo(function ProgressBar({
  cart,
  threshold,
  ltThresholdText,
  gteThresholdText,
  progressBarColor,
}: {
  cart: CartInfo | undefined;
  threshold: ProgressBarType["threshold"];
  ltThresholdText: ProgressBarType["ltThresholdText"];
  gteThresholdText: ProgressBarType["gteThresholdText"];
  progressBarColor: ProgressBarType["progressBarColor"];
}) {
  const progressData = useThresholdProgress({ cart, threshold });

  if (!progressData) {
    return null;
  }

  const { amountLeft, formattedAmountLeft, progressPercent } = progressData;

  if (amountLeft === 0) {
    return (
      <Flex w="full">
        <Text fontSize="lg" fontWeight="regular">
          {gteThresholdText}
        </Text>
      </Flex>
    );
  }

  const displayText = ltThresholdText.replace(
    THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER,
    formattedAmountLeft,
  );

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="md" fontWeight="medium">
        {displayText}
      </Text>
      <div
        style={{
          backgroundColor: "var(--redo-colors-gray-true-200)",
          width: "100%",
          height: "16px",
          borderRadius: "var(--border-radius-sm)",
          display: "grid",
        }}
      >
        <div style={{ gridRow: 1, gridColumn: 1 }}>
          <div
            style={{
              backgroundColor: progressBarColor,
              width: `${progressPercent}%`,
              height: "100%",
              borderRadius: "var(--border-radius-sm)",
              transition: "width 0.3s ease-in-out",
            }}
          />
        </div>
      </div>
    </Flex>
  );
});
